import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DateTime } from "luxon";
import Input from "../../Shared/Input";
import Toggle from "../../Shared/Toggle";
import TimeSelector from "../../Shared/TimeSelector";
import FormFooter from "../../Shared/FormFooter";
import FormWrapper from "../../Shared/FormWrapper";
import AlertRed from "../../Shared/AlertRed";
import { getAvailabilities } from "../../../api/availabilityApi";
import { mapApiError } from "../../../utils/formErrorMapper";
import { FORM_MODE } from "../../../config/constants";
import useScrollToFirstError from "../../../hooks/useScrollToFirstError";
import useIsMobile from "../../../hooks/useIsMobile";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

// Converts the form's stored yyyy-MM-dd string into a Date for react-datepicker's
// `selected` prop. react-datepicker interprets `selected` in the browser's LOCAL
// timezone, so we parse the string at local midnight (Luxon's default zone) — a
// plain `new Date("yyyy-MM-dd")` or a UTC parse would land on UTC midnight and
// render as the previous day for negative-offset users (e.g. the Americas). This
// mirrors jsDateToDateString below so the read/write round-trip is day-stable.
const dateStringToJsDate = (value) => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toJSDate() : null;
};

// Inverse of dateStringToJsDate — reads the local wall-clock date the picker
// shows back into the yyyy-MM-dd string the form stores.
const jsDateToDateString = (value) =>
  value ? DateTime.fromJSDate(value).toFormat("yyyy-MM-dd") : "";

// Date field styled to mirror TimeSelector's input so the timed row
// (Date / Start time / End time) shares one height, radius, border, and label
// treatment. Branches on viewport: mobile keeps the native <input type="date">
// (fastest, zoom-safe touch affordance — no OS calendar popup needed); desktop
// renders react-datepicker so the calendar is legible instead of the tiny,
// unstyleable OS-drawn native popup. Both branches read/write the same
// yyyy-MM-dd string the form (and buildPayload) expect, so RHF validation and
// the end-date `min`/`validate` rule stay identical either way.
const TimeOffDateField = ({
  id,
  label,
  name,
  value,
  onChange,
  min,
  errormessage,
  register,
  registerOptions,
}) => {
  const isMobile = useIsMobile();
  const hasError = !!errormessage;
  const { ref: registerRef, ...registerProps } = register(name, registerOptions);

  if (isMobile) {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-ink-900">
          {label}
        </label>
        <div className="mt-2">
          <input
            id={id}
            type="date"
            min={min}
            ref={registerRef}
            className={`block w-full min-h-[44px] rounded-md bg-white py-2.5 pl-2 pr-2 text-base text-ink-900 outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 ${
              hasError
                ? "outline-signal-red-500 focus:outline-signal-red-500"
                : "outline-ink-200 focus:outline-accent-500"
            }`}
            {...registerProps}
          />
          {hasError ? (
            <p className="mt-2 text-sm text-signal-red-700">{errormessage}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink-900">
        {label}
      </label>
      <div className="mt-2">
        {/* Hidden input keeps this field registered with RHF (required/validate,
            isDirty, errors) even though react-datepicker can't take register()'s
            ref directly — DatePicker drives its value via setValue below. */}
        <input type="hidden" ref={registerRef} {...registerProps} />
        <DatePicker
          id={id}
          selected={dateStringToJsDate(value)}
          onChange={(date) => onChange(name, jsDateToDateString(date))}
          // Click-to-pick only: suppress free-typing into the input (which could
          // silently clear or mis-parse the value). preventDefault makes
          // react-datepicker's typing path bail out early while leaving
          // click-to-open and day-selection fully functional (readOnly would
          // disable the calendar too).
          onChangeRaw={(e) => e.preventDefault()}
          minDate={dateStringToJsDate(min)}
          dateFormat="MMM d, yyyy"
          placeholderText="Select a date"
          wrapperClassName="w-full"
          className={`block w-full rounded-md bg-white py-1.5 pl-2 pr-2 text-base text-ink-900 outline outline-1 -outline-offset-1 placeholder:text-ink-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 ${
            hasError
              ? "outline-signal-red-500 focus:outline-signal-red-500"
              : "outline-ink-200 focus:outline-accent-500"
          }`}
        />
        {hasError ? (
          <p className="mt-2 text-sm text-signal-red-700">{errormessage}</p>
        ) : null}
      </div>
    </div>
  );
};

// Derives the anchor timezone default + whether to show the multi-tz helper
// from the provider's availabilities. Falls back to the browser tz when the
// provider has none yet.
const useAnchorTimeZone = () => {
  const { data: availabilities } = useQuery({
    queryKey: ["availabilities"],
    queryFn: getAvailabilities,
  });

  return useMemo(() => {
    const active = (availabilities || []).filter((a) => a.isActive);
    const distinctZones = [
      ...new Set(active.map((a) => a.timeZoneId).filter(Boolean)),
    ];
    const defaultZone =
      distinctZones[0] || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      defaultZone,
      hasMultipleZones: distinctZones.length > 1,
    };
  }, [availabilities]);
};

// Converts a stored UTC block into the wall-clock form field values for
// edit-mode prefill, using the block's own anchor timeZoneId.
const blockToFormValues = (block) => {
  if (!block) return null;
  const start = DateTime.fromISO(block.startTime, { zone: block.timeZoneId });
  const end = DateTime.fromISO(block.endTime, { zone: block.timeZoneId });

  if (block.allDay) {
    return {
      allDay: true,
      date: start.toFormat("yyyy-MM-dd"),
      endDate: end.toFormat("yyyy-MM-dd"),
      isMultiDay: !start.hasSame(end, "day"),
      startClock: { id: "start", name: "09:00" },
      endClock: { id: "end", name: "17:00" },
      reason: block.reason || "",
    };
  }

  return {
    allDay: false,
    date: start.toFormat("yyyy-MM-dd"),
    endDate: "",
    isMultiDay: false,
    startClock: { id: "start", name: start.toFormat("HH:mm") },
    endClock: { id: "end", name: end.toFormat("HH:mm") },
    reason: block.reason || "",
  };
};

const TimeOffForm = ({
  defaultBlock,
  mode,
  createBlock,
  updateBlock,
  onCancelHandler,
  setIsFormDirty,
}) => {
  const { defaultZone, hasMultipleZones } = useAnchorTimeZone();
  const [banner, setBanner] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitPayload, setLastSubmitPayload] = useState(null);

  const timeZoneId = defaultBlock?.timeZoneId || defaultZone;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    setError,
  } = useForm({
    defaultValues: blockToFormValues(defaultBlock) || {
      allDay: false,
      date: today(),
      endDate: "",
      isMultiDay: false,
      startClock: { id: "start", name: "09:00" },
      endClock: { id: "end", name: "17:00" },
      reason: "",
    },
  });

  const allDay = watch("allDay");
  const isMultiDay = watch("isMultiDay");
  const date = watch("date");
  const endDate = watch("endDate");
  const startClock = watch("startClock");
  const endClock = watch("endClock");

  useEffect(() => {
    setIsFormDirty(isDirty);
  }, [isDirty, setIsFormDirty]);

  // Build the endpoint-shaped payload from form state (mirrors the two
  // POST/PUT body shapes described by the /time-off contract).
  const buildPayload = (data) => {
    if (data.allDay) {
      return {
        allDay: true,
        startDate: data.date,
        endDate: data.isMultiDay && data.endDate ? data.endDate : data.date,
        timeZoneId,
        reason: data.reason?.trim() || undefined,
      };
    }
    return {
      allDay: false,
      date: data.date,
      startClock: data.startClock.name,
      endClock: data.endClock.name,
      timeZoneId,
      reason: data.reason?.trim() || undefined,
    };
  };

  const runSubmit = async (data) => {
    if (isSubmitting) return;
    setBanner(null);
    setLastSubmitPayload(data);
    setIsSubmitting(true);
    try {
      const payload = buildPayload(data);
      if (mode === FORM_MODE.CREATE) {
        await createBlock(payload);
      } else {
        await updateBlock({ id: defaultBlock._id, payload });
      }
    } catch (err) {
      const result = mapApiError(err, { setError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    if (lastSubmitPayload) runSubmit(lastSubmitPayload);
  };

  useScrollToFirstError(errors);

  // Client-side guard: end time must be after start time for timed blocks —
  // stated plainly per §5a, checked before submit so it never round-trips.
  const timedRangeInvalid =
    !allDay &&
    startClock?.name &&
    endClock?.name &&
    startClock.name >= endClock.name;

  return (
    <FormWrapper onSubmit={handleSubmit(runSubmit)}>
      {banner ? (
        <div className="mb-4">
          <AlertRed
            title={banner.title}
            message={banner.message}
            onRetry={handleRetry}
          />
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
        <div className="col-span-full flex items-center justify-between rounded-card border border-ink-100 bg-cream-100 p-4">
          <div>
            <p className="text-sm font-medium text-ink-900">All day</p>
            <p className="mt-0.5 text-sm text-ink-500">
              Block a full day or a range of days instead of a time window.
            </p>
          </div>
          <Toggle
            label={null}
            srLabel="All day"
            checked={allDay}
            onChange={(checked) =>
              setValue("allDay", checked, { shouldDirty: true })
            }
          />
        </div>

        {allDay ? (
          <>
            <div className="col-span-full sm:col-span-3">
              <TimeOffDateField
                id="date"
                label="Starts on"
                name="date"
                value={date}
                onChange={(name, value) =>
                  setValue(name, value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                errormessage={errors?.date?.message}
                register={register}
                registerOptions={{ required: "Start date is required" }}
              />
            </div>
            {isMultiDay ? (
              <div className="col-span-full sm:col-span-3">
                <TimeOffDateField
                  id="endDate"
                  label="Ends on"
                  name="endDate"
                  value={endDate}
                  onChange={(name, value) =>
                    setValue(name, value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  min={date}
                  errormessage={errors?.endDate?.message}
                  register={register}
                  registerOptions={{
                    required: "End date is required",
                    validate: (value) =>
                      !date || !value || value >= date
                        ? true
                        : "End date must be on or after the start date",
                  }}
                />
              </div>
            ) : (
              <div className="col-span-full flex sm:col-span-3 sm:items-end">
                <button
                  type="button"
                  onClick={() =>
                    setValue("isMultiDay", true, { shouldDirty: true })
                  }
                  className="mt-2 text-sm font-medium text-accent-600 hover:text-accent-700 sm:mb-2.5 sm:mt-0"
                >
                  Ends on a different day
                </button>
              </div>
            )}
            {hasMultipleZones && (
              <p className="col-span-full text-sm text-ink-500">
                All-day times use {timeZoneId}.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="col-span-full sm:col-span-2">
              <TimeOffDateField
                id="date"
                label="Date"
                name="date"
                value={date}
                onChange={(name, value) =>
                  setValue(name, value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                errormessage={errors?.date?.message}
                register={register}
                registerOptions={{ required: "Date is required" }}
              />
            </div>
            <div className="col-span-full sm:col-span-2">
              <TimeSelector
                label="Start time"
                value={startClock}
                onSelect={(value) =>
                  setValue("startClock", value, { shouldDirty: true })
                }
              />
            </div>
            <div className="col-span-full sm:col-span-2">
              <TimeSelector
                label="End time"
                value={endClock}
                timeDataFrom={startClock?.name}
                onSelect={(value) =>
                  setValue("endClock", value, { shouldDirty: true })
                }
              />
              {timedRangeInvalid && (
                <p className="mt-2 text-sm text-signal-red-700">
                  End time must be after the start time.
                </p>
              )}
            </div>
          </>
        )}

        <div className="col-span-full">
          <Input
            id="reason"
            label="Reason"
            optional
            placeholder="e.g. Vacation, Dentist"
            errormessage={errors?.reason?.message}
            {...register("reason", {
              maxLength: {
                value: 200,
                message: "Reason must be 200 characters or fewer",
              },
            })}
          />
        </div>
      </div>

      <FormFooter
        primaryCaption={
          mode === FORM_MODE.CREATE ? "Add time off" : "Save changes"
        }
        secondaryCaption="Cancel"
        secondaryClickHandler={onCancelHandler}
        isLoading={isSubmitting}
        disabled={timedRangeInvalid}
        requiresVerification={false}
      />
    </FormWrapper>
  );
};

export default TimeOffForm;
